'use client';

import { useState } from 'react';
import Link from 'next/link';

interface NewsletterSignupProps {
  className?: string;
  variant?: 'inline' | 'card';
  showTitle?: boolean;
}

export default function NewsletterSignup({
  className = '',
  variant = 'card',
  showTitle = true
}: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrorMessage('Please enter your email address.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('/api/public/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          firstName: firstName.trim(),
          acceptsTerms,
          _honeypot: '', // Honeypot field (empty for legitimate users)
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setEmail('');
        setFirstName('');
        setAcceptsTerms(false);
      } else {
        setStatus('error');
        // Only show user-friendly messages, not raw API errors
        setErrorMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Unable to connect. Please check your internet connection and try again.');
    }
  };

  const isInline = variant === 'inline';

  const containerClasses = isInline
    ? `${className}`
    : `bg-background/50 border border-border rounded-lg p-6 ${className}`;

  return (
    <div className={containerClasses}>
      {showTitle && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📬</span>
            <h3 className="text-xl font-bold text-foreground">
              The CTO Weekly
            </h3>
          </div>
          <p className="text-foreground-secondary text-sm mb-3">
            Join 5,000+ technical leaders getting:
          </p>
          <ul className="text-foreground-secondary text-sm space-y-1 mb-4">
            <li className="flex items-center gap-2">
              <span className="text-accent">✓</span>
              One actionable framework per week
            </li>
            <li className="flex items-center gap-2">
              <span className="text-accent">✓</span>
              Tool recommendations from real CTOs
            </li>
            <li className="flex items-center gap-2">
              <span className="text-accent">✓</span>
              Interview questions & hiring tips
            </li>
          </ul>
        </div>
      )}

      {status === 'success' ? (
        <div
          role="status"
          aria-live="polite"
          className="bg-accent-muted border border-border rounded-lg p-4"
        >
          <p className="text-accent font-medium mb-2">
            ✓ Almost there!
          </p>
          <p className="text-foreground-secondary text-sm">
            Please check your email to confirm your subscription. We use double opt-in to ensure you really want to hear from us.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First Name (Optional) */}
          <div>
            <label htmlFor="newsletter-firstName" className="block text-sm font-medium text-foreground-secondary mb-1">
              First Name <span className="text-foreground-secondary">(optional)</span>
            </label>
            <input
              type="text"
              id="newsletter-firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
              disabled={status === 'loading'}
              maxLength={50}
            />
          </div>

          {/* Email (Required) */}
          <div>
            <label htmlFor="newsletter-email" className="block text-sm font-medium text-foreground-secondary mb-1">
              Email Address <span className="text-accent">*</span>
            </label>
            <input
              type="email"
              id="newsletter-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
              disabled={status === 'loading'}
            />
          </div>

          {/* Terms Acceptance Checkbox */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="newsletter-terms"
              checked={acceptsTerms}
              onChange={(e) => setAcceptsTerms(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-border bg-background text-accent focus:ring-2 focus:ring-ring/50 cursor-pointer"
              disabled={status === 'loading'}
            />
            <label htmlFor="newsletter-terms" className="text-sm text-foreground-secondary cursor-pointer">
              I agree to receive emails from The Art of CTO and accept the{' '}
              <Link href="/privacy" className="text-accent underline">
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link href="/terms" className="text-accent underline">
                Terms of Service
              </Link>
              . You can unsubscribe at any time.
            </label>
          </div>

          {/* Error Message */}
          {status === 'error' && errorMessage && (
            <div
              role="alert"
              aria-live="polite"
              className="bg-red-500/10 border border-red-500/30 rounded-lg p-3"
            >
              <p className="text-red-400 text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'loading' || !acceptsTerms}
            className="w-full bg-accent hover:bg-accent-hover disabled:bg-accent-muted0 disabled:cursor-not-allowed text-accent-foreground font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </button>

          {/* GDPR Notice */}
          <p className="text-xs text-foreground-secondary text-center">
            We respect your privacy. We use Brevo to manage subscriptions and send emails.
            Your data is stored securely and never shared with third parties.
          </p>
        </form>
      )}
    </div>
  );
}
